import { useCallback, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "../../Form/TextField";
import Select from "../../Form/Select";
import ColorField from "../../Form/ColorField";
import Button from "../../Buttons/Button";
import FontFamilySelect from "../../../features/creativeTemplates/editor/FontFamilySelect";
import FontFaceSelect from "../../../features/creativeTemplates/editor/FontFaceSelect";
import TextStyleSelect from "../../../features/creativeTemplates/editor/TextStyleSelect";
import TextOrientationSelect from "../../../features/creativeTemplates/editor/TextOrientationSelect";
import TextStrokeFields from "../../../features/creativeTemplates/editor/TextStrokeFields";
import TextBackgroundFields from "../../../features/creativeTemplates/editor/TextBackgroundFields";
import TextSpacingFields from "../../../features/creativeTemplates/editor/TextSpacingFields";
import TextInlineReplacementsFields from "../../../features/creativeTemplates/editor/TextInlineReplacementsFields";
import LayerAlignToolbar from "../../../features/creativeTemplates/editor/LayerAlignToolbar";
import TextShadowFields from "../../../features/creativeTemplates/editor/TextShadowFields";
import {
  DEFAULT_SHADOW_ANGLE,
  TEXT_STYLE,
} from "../../../features/creativeTemplates/utils/textStyle";
import {
  isVerticalTextOrientation,
  resolveTextOrientation,
} from "../../../features/creativeTemplates/utils/textOrientation";
import { overlayHighlightInspector } from "../../../features/creativeTemplates/editor/useTextHighlightSelection";
import { useTranslationApp } from "../../../lib/i18next";
import {
  TEMPLATES_ALIGN,
  TEMPLATES_ALIGN_CENTER,
  TEMPLATES_ALIGN_LEFT,
  TEMPLATES_ALIGN_RIGHT,
  TEMPLATES_CHANGE_ASSET,
  TEMPLATES_FIXED_VALUE_PLACEHOLDER,
  TEMPLATES_LAYER_TEXT,
  TEMPLATES_STYLE,
  TEMPLATES_FONT_COLOR,
  TEMPLATES_FONT_SIZE,
  TEMPLATES_LAYER_NAME,
  TEMPLATES_NO_SELECTION,
  TEMPLATES_POS_H,
  TEMPLATES_POS_W,
  TEMPLATES_POS_X,
  TEMPLATES_POS_Y,
  TEMPLATES_POSITION,
  TEMPLATES_PROPERTIES,
  TEMPLATES_SELECT_ASSET,
  TEMPLATES_SHAPE_FILL,
  TEMPLATES_SHAPE_RADIUS,
  TEMPLATES_SIZE,
} from "../../../i18n/keysTranslations";
import {
  RESIZABLE_MULTILINE_MIN_ROWS,
  resizableMultilineSx,
} from "../../../features/creativeTemplates/editor/canvasWorkspace";

const optionalRules = { required: false };

const emptyInspector = {
  layerName: "",
  fixedValue: "",
  fontAssetRef: "",
  fontFaceRef: "",
  fontWeight: "",
  fontSize: "",
  lineHeight: "",
  letterSpacing: "",
  strokeWidth: "",
  strokeColor: "#000000",
  shadowColor: "#000000",
  shadowAngle: DEFAULT_SHADOW_ANGLE,
  shadowDistance: "",
  shadowBlur: "",
  backgroundPadding: "",
  backgroundRadius: "",
  backgroundColor: "#000000",
  textStyle: TEXT_STYLE.NORMAL,
  color: "",
  align: "left",
  orientation: "horizontal",
  fill: "",
  radius: "",
  posX: "",
  posY: "",
  posW: "",
  posH: "",
};

const inspectorFromItem = ({ item }) => {
  if (!item) return emptyInspector;
  const node = item.node;
  const box = item.box || {};
  return {
    ...emptyInspector,
    layerName: node.name || "",
    fixedValue: node.source?.value || "",
    fontAssetRef: node.style?.fontAssetRef || "",
    fontFaceRef: node.style?.fontFaceRef || "",
    fontWeight: node.style?.fontWeight ?? "",
    fontSize: node.style?.fontSize ?? "",
    lineHeight: node.style?.lineHeight ?? "",
    letterSpacing: node.style?.letterSpacing ?? "",
    strokeWidth: node.style?.strokeWidth ?? "",
    strokeColor: node.style?.strokeColor || "#000000",
    shadowColor: node.style?.shadowColor || "#000000",
    shadowAngle: node.style?.shadowAngle ?? DEFAULT_SHADOW_ANGLE,
    shadowDistance: node.style?.shadowDistance ?? "",
    shadowBlur: node.style?.shadowBlur ?? "",
    backgroundPadding: node.style?.backgroundPadding ?? "",
    backgroundRadius: node.style?.backgroundRadius ?? "",
    backgroundColor: node.style?.backgroundColor || "#000000",
    textStyle: node.style?.textStyle || TEXT_STYLE.NORMAL,
    color: node.style?.color || "",
    align: node.style?.align || "left",
    orientation: node.style?.orientation || "horizontal",
    fill: node.shape?.fill || "",
    radius: node.shape?.radius ?? "",
    posX: Math.round(box.x || 0),
    posY: Math.round(box.y || 0),
    posW: Math.round(box.width || 0),
    posH: Math.round(box.height || 0),
  };
};

const FieldGroup = ({ title, children }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
    <Typography
      variant="body2"
      sx={{ fontWeight: 500, color: "text.secondary", fontSize: "13px" }}
    >
      {title}
    </Typography>
    <Box sx={{ display: "flex", gap: 1 }}>{children}</Box>
  </Box>
);

const INSPECTOR_TEXT_FIELD_KEYS = new Set([
  "name",
  "fixedValue",
  "originText",
  "expression",
  "promptTemplate",
]);

const propertiesCardSx = {
  height: "100%",
  minHeight: 0,
  overflowY: "auto",
  px: 2,
  py: 2.5,
};

const InstancePropertiesPanel = ({
  selectedItem,
  isUploadingAsset = false,
  isTextRangeSelected = false,
  selectedPhrase = "",
  alignOnly = false,
  onAlign,
  onChangeField,
  onChangeBox,
  onUploadAsset,
  onUploadInlineReplacementAsset,
  inlineReplacementUrls = {},
}) => {
  const { t } = useTranslationApp();
  const assetFileInputRef = useRef(null);
  const inlineReplacementFileInputRef = useRef(null);
  const pendingInlineSlotIdRef = useRef(null);
  const node = selectedItem?.node;
  const isBackground = Boolean(selectedItem?.isBackground);
  const {
    control,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: emptyInspector,
  });

  const skipInspectorFormResetRef = useRef({ skip: false, nodeID: null });
  const nodeID = node?.id ?? null;

  useEffect(() => {
    const pendingInspectorReset = skipInspectorFormResetRef.current;
    if (pendingInspectorReset.skip && pendingInspectorReset.nodeID === nodeID) {
      skipInspectorFormResetRef.current = { skip: false, nodeID: null };
      return;
    }
    skipInspectorFormResetRef.current = { skip: false, nodeID: null };

    reset({
      ...inspectorFromItem({ item: selectedItem }),
      // Lay the selected phrase's own styles over the layer's, so the panel
      // shows what the canvas paints for it.
      ...(selectedPhrase
        ? overlayHighlightInspector({ node, phrase: selectedPhrase })
        : {}),
    });
  }, [node, nodeID, reset, selectedItem, selectedPhrase]);

  const onChangeFieldFromInspector = useCallback(
    (args) => {
      const committed = onChangeField?.(args);
      if (!committed || !INSPECTOR_TEXT_FIELD_KEYS.has(args?.key)) return;
      skipInspectorFormResetRef.current = { skip: true, nodeID };
    },
    [nodeID, onChangeField]
  );

  if (alignOnly) {
    return (
      <Box sx={propertiesCardSx}>
        <Typography sx={{ fontWeight: 600, mb: 1 }}>
          {t(TEMPLATES_PROPERTIES)}
        </Typography>
        <LayerAlignToolbar onAlign={onAlign} />
      </Box>
    );
  }

  if (!selectedItem) {
    return (
      <Box sx={propertiesCardSx}>
        <Typography sx={{ fontWeight: 600, mb: 1 }}>
          {t(TEMPLATES_PROPERTIES)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t(TEMPLATES_NO_SELECTION)}
        </Typography>
      </Box>
    );
  }

  // The only controls a highlight can override, shared verbatim with the
  // reduced panel below so both views can never drift apart.
  const highlightableFontFields = (
    <>
      <FontFamilySelect
        control={control}
        errors={errors}
        onChangeField={onChangeFieldFromInspector}
      />
      <FontFaceSelect
        control={control}
        errors={errors}
        onChangeField={onChangeFieldFromInspector}
      />
      <TextStyleSelect
        control={control}
        errors={errors}
        onChangeField={onChangeFieldFromInspector}
        label={t(TEMPLATES_STYLE)}
      />
    </>
  );
  const highlightablePaintFields = (
    <>
      <ColorField
        control={control}
        errors={errors}
        name="color"
        label={t(TEMPLATES_FONT_COLOR)}
        fullWidth
        rules={optionalRules}
        actionsChange={(value) =>
          onChangeFieldFromInspector({ key: "color", value })
        }
      />
      <TextStrokeFields
        control={control}
        errors={errors}
        onChangeField={onChangeFieldFromInspector}
      />
      <TextBackgroundFields
        control={control}
        errors={errors}
        onChangeField={onChangeFieldFromInspector}
      />
    </>
  );

  // A selected range styles only that phrase, so everything that can only
  // apply to the whole layer is hidden instead of silently doing nothing.
  if (isTextRangeSelected) {
    return (
      <Box sx={propertiesCardSx}>
        <Typography sx={{ fontWeight: 600, mb: 1 }}>
          {t(TEMPLATES_PROPERTIES)}
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {highlightableFontFields}
          {highlightablePaintFields}
        </Box>
      </Box>
    );
  }

  const type = node.type;
  const isHorizontalText =
    type === "text" &&
    !isVerticalTextOrientation(resolveTextOrientation(node.style?.orientation));
  const showInlineReplacements =
    isHorizontalText && typeof onUploadInlineReplacementAsset === "function";

  return (
    <Box
      sx={{
        height: "100%",
        minHeight: 0,
        overflowY: "auto",
        px: 2,
        py: 2.5,
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
      }}
    >
      <Typography sx={{ fontWeight: 500 }}>
        {t(TEMPLATES_PROPERTIES)}
      </Typography>
      <TextField
        control={control}
        errors={errors}
        name="layerName"
        label={t(TEMPLATES_LAYER_NAME)}
        variant="filled"
        fullWidth
        rules={optionalRules}
        actionsChange={(value) =>
          onChangeFieldFromInspector({ key: "name", value })
        }
      />
      {type === "text" ? (
        <>
          <TextField
            control={control}
            errors={errors}
            name="fixedValue"
            label={t(TEMPLATES_LAYER_TEXT)}
            placeholder={t(TEMPLATES_FIXED_VALUE_PLACEHOLDER)}
            variant="filled"
            fullWidth
            multiline
            minRows={RESIZABLE_MULTILINE_MIN_ROWS}
            rules={optionalRules}
            InputLabelProps={{ shrink: true }}
            sx={resizableMultilineSx}
            actionsChange={(value) =>
              onChangeFieldFromInspector({ key: "fixedValue", value })
            }
          />
          {highlightableFontFields}
          <TextField
            control={control}
            errors={errors}
            name="fontSize"
            label={t(TEMPLATES_FONT_SIZE)}
            variant="filled"
            type="number"
            fullWidth
            rules={optionalRules}
            InputLabelProps={{ shrink: true }}
            actionsChange={(value) =>
              onChangeFieldFromInspector({
                key: "fontSize",
                value: value === "" ? undefined : Number(value),
              })
            }
          />
          <TextSpacingFields
            control={control}
            errors={errors}
            onChangeField={onChangeFieldFromInspector}
          />
          {highlightablePaintFields}
          <TextShadowFields
            control={control}
            errors={errors}
            onChangeField={onChangeFieldFromInspector}
          />
          <Select
            control={control}
            errors={errors}
            name="align"
            label={t(TEMPLATES_ALIGN)}
            variant="filled"
            fullWidth
            rules={optionalRules}
            actionsChange={(value) =>
              onChangeFieldFromInspector({ key: "align", value })
            }
            options={[
              { value: "left", label: t(TEMPLATES_ALIGN_LEFT) },
              { value: "center", label: t(TEMPLATES_ALIGN_CENTER) },
              { value: "right", label: t(TEMPLATES_ALIGN_RIGHT) },
            ]}
          />
          <TextOrientationSelect
            control={control}
            errors={errors}
            onChangeField={onChangeFieldFromInspector}
          />
        </>
      ) : null}
      {type === "image" && !isBackground ? (
        <>
          {selectedItem.url ? (
            <Box
              sx={{
                width: "100%",
                aspectRatio: "1 / 1",
                borderRadius: 2,
                border: "1px solid #E4E4E7",
                bgcolor: "action.hover",
                overflow: "hidden",
              }}
            >
              <Box
                component="img"
                src={selectedItem.url}
                alt=""
                sx={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            </Box>
          ) : null}
          <input
            ref={assetFileInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = "";
              if (!file || !onUploadAsset) return;
              onUploadAsset({ file });
            }}
          />
          <Button
            color="secondary"
            loading={isUploadingAsset}
            onClick={() => assetFileInputRef.current?.click()}
          >
            {t(
              selectedItem.url ? TEMPLATES_CHANGE_ASSET : TEMPLATES_SELECT_ASSET
            )}
          </Button>
        </>
      ) : null}
      {type === "shape" ? (
        <>
          <ColorField
            control={control}
            errors={errors}
            name="fill"
            label={t(TEMPLATES_SHAPE_FILL)}
            fullWidth
            rules={optionalRules}
            actionsChange={(value) =>
              onChangeFieldFromInspector({ key: "fill", value })
            }
          />
          <TextField
            control={control}
            errors={errors}
            name="radius"
            label={t(TEMPLATES_SHAPE_RADIUS)}
            variant="filled"
            type="number"
            fullWidth
            rules={optionalRules}
            actionsChange={(value) =>
              onChangeFieldFromInspector({
                key: "radius",
                value: value === "" ? undefined : Number(value),
              })
            }
          />
        </>
      ) : null}
      <FieldGroup title={t(TEMPLATES_POSITION)}>
        <TextField
          control={control}
          errors={errors}
          name="posX"
          label={t(TEMPLATES_POS_X)}
          variant="filled"
          type="number"
          fullWidth
          disabled={isBackground}
          rules={optionalRules}
          InputLabelProps={{ shrink: true }}
          actionsChange={(value) =>
            onChangeBox({
              x: value === "" ? 0 : Number(value),
            })
          }
        />
        <TextField
          control={control}
          errors={errors}
          name="posY"
          label={t(TEMPLATES_POS_Y)}
          variant="filled"
          type="number"
          fullWidth
          disabled={isBackground}
          rules={optionalRules}
          InputLabelProps={{ shrink: true }}
          actionsChange={(value) =>
            onChangeBox({
              y: value === "" ? 0 : Number(value),
            })
          }
        />
      </FieldGroup>
      <FieldGroup title={t(TEMPLATES_SIZE)}>
        <TextField
          control={control}
          errors={errors}
          name="posW"
          label={t(TEMPLATES_POS_W)}
          variant="filled"
          type="number"
          fullWidth
          disabled={isBackground}
          rules={optionalRules}
          InputLabelProps={{ shrink: true }}
          actionsChange={(value) =>
            onChangeBox({
              width: value === "" ? 0 : Number(value),
            })
          }
        />
        <TextField
          control={control}
          errors={errors}
          name="posH"
          label={t(TEMPLATES_POS_H)}
          variant="filled"
          type="number"
          fullWidth
          disabled={isBackground}
          rules={optionalRules}
          InputLabelProps={{ shrink: true }}
          actionsChange={(value) =>
            onChangeBox({
              height: value === "" ? 0 : Number(value),
            })
          }
        />
      </FieldGroup>
      {showInlineReplacements ? (
        <>
          <input
            ref={inlineReplacementFileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            hidden
            onChange={(event) => {
              const file = event.target.files?.[0];
              const slotId = pendingInlineSlotIdRef.current;
              pendingInlineSlotIdRef.current = null;
              event.target.value = "";
              if (!file || !slotId) return;
              onUploadInlineReplacementAsset({ file, slotId });
            }}
          />
          <TextInlineReplacementsFields
            replacements={node.style?.inlineReplacements}
            urls={inlineReplacementUrls}
            disabled={isUploadingAsset}
            onChangeReplacements={(value) =>
              onChangeFieldFromInspector({ key: "inlineReplacements", value })
            }
            onSelectAsset={(slotId) => {
              pendingInlineSlotIdRef.current = slotId;
              inlineReplacementFileInputRef.current?.click();
            }}
          />
        </>
      ) : null}
    </Box>
  );
};

export default InstancePropertiesPanel;
